import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from '../common/Modal'
import { onSessionKilled, resetForm } from '../../state/slice/form'
import { Grid, GridColumn, GridRow } from 'semantic-ui-react'
import { Button } from 'react-dxd-components'
import { useNavigate } from 'react-router-dom'
import { useIdleTimer } from 'react-idle-timer'
import PropTypes from 'prop-types'

/**
 * @param idleDurationInMinutes The allowed time for a user to be idle, before resetting the form state (this includes the warn duration).
 * @param warnDurationInMinutes Once the idle timer is up, the allowed time for user to interact with the warning modal.
 */
export default function IdleSessionTimeout ({
  idleDurationInMinutes = 30,
  warnDurationInMinutes = 25
}) {
  const IDLE_DURATION_MS = idleDurationInMinutes * 60_000
  const WARN_DURATION_SEC = warnDurationInMinutes * 60

  const sessionIsLive = useSelector(state => state.form.context.sessionLive)
  const dispatch = useDispatch()

  const [warn, setWarn] = useState(false)
  const [seconds, setSeconds] = useState(WARN_DURATION_SEC)
  const [killSession, setKillSession] = useState(false)
  const navigate = useNavigate()

  const secondsDisplay = seconds % 60
  const minutesDisplay = ((seconds - secondsDisplay) / 60) % 60

  const { activate, start, getRemainingTime } = useIdleTimer({
    timeout: IDLE_DURATION_MS,
    promptBeforeIdle: WARN_DURATION_SEC * 1000,
    startManually: true,
    stopOnIdle: true,
    eventsThrottle: 500,
    onPrompt: () => {
      setWarn(true)
    },
    onIdle: () => setKillSession(true),
    onActive: () => {
      if (warn) {
        setWarn(false)
      }
    },
  })

  useEffect(() => {
    if (warn) {
      const secondsRemainingInterval = setInterval(() => {
        setSeconds(Math.ceil(getRemainingTime() / 1000))
      }, 500)
      return () => clearInterval(secondsRemainingInterval)
    }
  },[warn, getRemainingTime])

  useEffect(() => {
    if (sessionIsLive) {
      start()
    }
    // eslint-disable-next-line
  }, [sessionIsLive])

  useEffect(() => {
    async function reset () {
      await dispatch(resetForm())
    }

    async function sessionKilled () {
      await reset().then(async () => {
        await dispatch(onSessionKilled())
      })
    }

    if (killSession) {
      setWarn(false)
      sessionKilled().then(() => {
        setKillSession(false)
        navigate('/home')
      })
    }
  }, [killSession, setKillSession, dispatch, navigate])

  const onContinue = () => {
    activate()
  }

  const timeDisplay = () => {
    return [
      [
        minutesDisplay > 0 && `${minutesDisplay} minute`,
        minutesDisplay > 1 && 's'
      ].filter(Boolean).join(''),
      `${secondsDisplay} seconds`,
    ].filter(Boolean).join(', ')
  }

  return <Modal
    id='idle-session-timeout'
    open={warn}
  >
    <Grid>
      <GridRow>
        <GridColumn width={16}>
          <h5>Session Expiration Warning</h5>
        </GridColumn>
        <GridColumn width={16} data-testid='session-expiration-warning'>
          To protect your personal information, your session will expire unless activity is detected
          in <b>{timeDisplay()}</b>.
        </GridColumn>
        <GridColumn computer={4} tablet={6} mobile={16} floated='right'>
          <Button onClick={onContinue}>Extend Session</Button>
        </GridColumn>
      </GridRow>
    </Grid>
  </Modal>

}

IdleSessionTimeout.propTypes = {
  idleDurationInMinutes: PropTypes.number,
  warnDurationInMinutes: PropTypes.number,
}